import React, { useRef, useState } from "react";
import { RotateCcw, Download, Undo2 } from "lucide-react";
import {
  RANGOLI_PALETTE,
  RANGOLI_STAMPS,
  SYMMETRY_OPTIONS,
  RANGOLI_BACKGROUNDS,
} from "../../data/rangoli";
import ShareArtwork from "./ShareArtwork";

const CANVAS = 600;
const CENTER = CANVAS / 2;

export default function RangoliActivity({ onSave }) {
  const svgRef = useRef(null);
  const [stampId, setStampId] = useState("petal");
  const [color, setColor] = useState(RANGOLI_PALETTE[0].value);
  const [size, setSize] = useState(1.4);
  const [symmetry, setSymmetry] = useState(8);
  const [bg, setBg] = useState(RANGOLI_BACKGROUNDS[0].value);
  const [placed, setPlaced] = useState([]); // [{ id, stampId, x, y, rotation, color, size }]

  const onTap = (e) => {
    const svg = svgRef.current;
    const pt = svg.createSVGPoint();
    const native = e.touches ? e.touches[0] : e;
    pt.x = native.clientX;
    pt.y = native.clientY;
    const ctm = svg.getScreenCTM();
    if (!ctm) return;
    const { x, y } = pt.matrixTransform(ctm.inverse());

    const dx = x - CENTER;
    const dy = y - CENTER;
    const r = Math.sqrt(dx * dx + dy * dy);
    const angle = Math.atan2(dy, dx);

    const newStamps = [];
    const n = Math.max(1, symmetry);
    for (let i = 0; i < n; i++) {
      const newAngle = angle + (Math.PI * 2 * i) / n;
      const nx = CENTER + r * Math.cos(newAngle);
      const ny = CENTER + r * Math.sin(newAngle);
      const rotation = (360 * i) / n;
      newStamps.push({
        id: `s-${Date.now()}-${i}-${Math.random().toString(36).slice(2, 6)}`,
        stampId,
        x: nx,
        y: ny,
        rotation,
        color,
        size,
      });
    }
    setPlaced((p) => [...p, ...newStamps]);
  };

  const undo = () => {
    // Pop the last "ring" of stamps (1 to N at a time depending on symmetry that was used)
    // Simplest: drop the last N where N = symmetry; otherwise just drop the last 1.
    setPlaced((p) => p.slice(0, Math.max(0, p.length - symmetry)));
  };

  const clear = () => setPlaced([]);

  const downloadArt = async () => {
    const svg = svgRef.current;
    if (!svg) return;
    const xml = new XMLSerializer().serializeToString(svg);
    const svg64 = btoa(unescape(encodeURIComponent(xml)));
    const img = new Image();
    img.onload = () => {
      const out = document.createElement("canvas");
      out.width = 1200;
      out.height = 1200;
      const ctx = out.getContext("2d");
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, 1200, 1200);
      ctx.drawImage(img, 0, 0, 1200, 1200);
      const link = document.createElement("a");
      link.download = "my-rangoli.png";
      link.href = out.toDataURL("image/png");
      link.click();
      if (onSave) onSave();
    };
    img.src = `data:image/svg+xml;base64,${svg64}`;
  };

  return (
    <div className="grid lg:grid-cols-12 gap-8 items-start" data-testid="rangoli-activity">
      {/* Canvas */}
      <div className="lg:col-span-8">
        <div className="relative w-full aspect-square rounded-2xl overflow-hidden border-4 border-[#E5DFD3] shadow-xl">
          <svg
            ref={svgRef}
            viewBox={`0 0 ${CANVAS} ${CANVAS}`}
            xmlns="http://www.w3.org/2000/svg"
            className="absolute inset-0 w-full h-full touch-none cursor-crosshair"
            onClick={onTap}
            onTouchStart={(e) => {
              e.preventDefault();
              onTap(e);
            }}
            data-testid="rangoli-canvas"
          >
            <rect width="100%" height="100%" fill={bg} />

            {/* Subtle dot grid */}
            <g opacity="0.18">
              {Array.from({ length: 13 }).map((_, ix) =>
                Array.from({ length: 13 }).map((_, iy) => (
                  <circle
                    key={`g-${ix}-${iy}`}
                    cx={50 + ix * (CANVAS - 100) / 12}
                    cy={50 + iy * (CANVAS - 100) / 12}
                    r="1.5"
                    fill={bg === "#F9F6F0" ? "#1E2A4F" : "#F9F6F0"}
                  />
                ))
              )}
            </g>

            {/* Center crosshair */}
            <g opacity="0.25">
              <line
                x1={CENTER - 10}
                y1={CENTER}
                x2={CENTER + 10}
                y2={CENTER}
                stroke={bg === "#F9F6F0" ? "#1E2A4F" : "#F9F6F0"}
                strokeWidth="1.5"
              />
              <line
                x1={CENTER}
                y1={CENTER - 10}
                x2={CENTER}
                y2={CENTER + 10}
                stroke={bg === "#F9F6F0" ? "#1E2A4F" : "#F9F6F0"}
                strokeWidth="1.5"
              />
            </g>

            {/* Symmetry axis guides */}
            {symmetry > 1 && (
              <g opacity="0.08">
                {Array.from({ length: symmetry }).map((_, i) => {
                  const a = (Math.PI * 2 * i) / symmetry;
                  return (
                    <line
                      key={`ax-${i}`}
                      x1={CENTER}
                      y1={CENTER}
                      x2={CENTER + (CANVAS / 2) * Math.cos(a)}
                      y2={CENTER + (CANVAS / 2) * Math.sin(a)}
                      stroke={bg === "#F9F6F0" ? "#1E2A4F" : "#F9F6F0"}
                      strokeWidth="1"
                    />
                  );
                })}
              </g>
            )}

            {/* Placed stamps */}
            {placed.map((s) => {
              const stamp = RANGOLI_STAMPS.find((st) => st.id === s.stampId);
              if (!stamp) return null;
              return (
                <g
                  key={s.id}
                  transform={`translate(${s.x},${s.y}) rotate(${s.rotation}) scale(${s.size})`}
                  color={s.color}
                  style={{ pointerEvents: "none" }}
                  data-testid={`rangoli-placed-${s.stampId}`}
                >
                  {stamp.svg}
                </g>
              );
            })}
          </svg>
        </div>
        <p className="mt-4 text-sm text-[#5C6A8F] text-center">
          Tap anywhere to place a stamp — it mirrors automatically around the centre.
        </p>
      </div>

      {/* Tools */}
      <div className="lg:col-span-4 space-y-5">
        {/* Symmetry */}
        <div>
          <p className="text-[10px] tracking-[0.24em] uppercase text-[#5C6A8F] mb-3">Symmetry</p>
          <div className="grid grid-cols-5 gap-1.5">
            {SYMMETRY_OPTIONS.map((s) => (
              <button
                key={s.id}
                data-testid={`rangoli-sym-${s.id}`}
                onClick={() => setSymmetry(s.id)}
                className={`px-2 py-2 rounded-lg border-2 text-xs font-medium transition-all ${
                  symmetry === s.id
                    ? "bg-[#E54B6A] text-[#F9F6F0] border-[#E54B6A]"
                    : "border-[#E5DFD3] text-[#5C6A8F] hover:border-[#E54B6A]"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {/* Stamp picker */}
        <div>
          <p className="text-[10px] tracking-[0.24em] uppercase text-[#5C6A8F] mb-3">Stamp</p>
          <div className="grid grid-cols-5 gap-2">
            {RANGOLI_STAMPS.map((st) => (
              <button
                key={st.id}
                data-testid={`rangoli-stamp-${st.id}`}
                onClick={() => setStampId(st.id)}
                aria-label={st.name}
                title={st.name}
                className={`aspect-square rounded-lg border-2 flex items-center justify-center transition-all ${
                  stampId === st.id
                    ? "border-[#E54B6A] bg-[#FFE5EE]"
                    : "border-[#E5DFD3] bg-white hover:border-[#E54B6A]"
                }`}
              >
                <svg viewBox="-20 -20 40 40" className="w-3/4 h-3/4" color={color}>
                  {st.svg}
                </svg>
              </button>
            ))}
          </div>
        </div>

        {/* Colours */}
        <div>
          <p className="text-[10px] tracking-[0.24em] uppercase text-[#5C6A8F] mb-3">Colour</p>
          <div className="grid grid-cols-7 gap-1.5">
            {RANGOLI_PALETTE.map((c) => (
              <button
                key={c.value}
                data-testid={`rangoli-color-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setColor(c.value)}
                aria-label={c.name}
                style={{ background: c.value }}
                className={`aspect-square rounded-md border-2 transition-transform ${
                  color === c.value
                    ? "scale-110 border-[#1E2A4F] ring-2 ring-[#1E2A4F]/30"
                    : "border-[#E5DFD3] hover:scale-105"
                }`}
              />
            ))}
          </div>
        </div>

        {/* Size */}
        <div>
          <p className="text-[10px] tracking-[0.24em] uppercase text-[#5C6A8F] mb-2">Stamp size</p>
          <input
            type="range"
            min="0.5"
            max="3"
            step="0.1"
            value={size}
            onChange={(e) => setSize(parseFloat(e.target.value))}
            data-testid="rangoli-size"
            className="w-full accent-[#E54B6A]"
          />
        </div>

        {/* Background */}
        <div>
          <p className="text-[10px] tracking-[0.24em] uppercase text-[#5C6A8F] mb-2">Background</p>
          <div className="flex gap-2">
            {RANGOLI_BACKGROUNDS.map((b) => (
              <button
                key={b.value}
                data-testid={`rangoli-bg-${b.name.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setBg(b.value)}
                aria-label={b.name}
                style={{ background: b.value }}
                className={`w-9 h-9 rounded-full border-2 transition-transform ${
                  bg === b.value
                    ? "scale-110 border-[#E54B6A] ring-2 ring-[#E54B6A]/30"
                    : "border-[#E5DFD3] hover:scale-105"
                }`}
              />
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <button
            data-testid="rangoli-undo"
            onClick={undo}
            className="inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-full border border-[#1E2A4F]/20 text-[#1E2A4F] hover:bg-[#1E2A4F] hover:text-[#F9F6F0] transition-colors duration-300 text-xs"
          >
            <Undo2 size={12} /> Undo
          </button>
          <button
            data-testid="rangoli-clear"
            onClick={clear}
            className="inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-full border border-[#1E2A4F]/20 text-[#1E2A4F] hover:bg-[#1E2A4F] hover:text-[#F9F6F0] transition-colors duration-300 text-xs"
          >
            <RotateCcw size={12} /> Clear
          </button>
        </div>

        <button
          data-testid="rangoli-download"
          onClick={downloadArt}
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-4 rounded-full bg-[#E54B6A] text-[#F9F6F0] hover:bg-[#1E2A4F] transition-colors duration-300 text-sm font-medium tracking-wide"
        >
          <Download size={16} /> Save my rangoli
        </button>
        <ShareArtwork
          artFormId="rangoli"
          artName="Rangoli"
          accent="#E54B6A"
          testIdPrefix="rangoli"
        />
      </div>
    </div>
  );
}
